/**
 * Prompts for AI Admin Intelligence (Phase 2J).
 *
 * Every figure the narrative may mention is produced deterministically by the
 * `ai-admin-intelligence` statistics endpoint. The model narrates — it never
 * computes, invents or contradicts a number.
 *
 * PII contract: the prompt is built exclusively from platform-wide aggregates.
 * No user ids, emails, organizer names, event titles or event ids may appear
 * here. Category names are the only free-text values, and they are platform
 * taxonomy, not personal data.
 *
 * The admin console is English-only today, so this prompt is deliberately
 * English-only: no locale scope was introduced for this migration.
 */

import type { AdminIntelligenceStats } from '../types';

export const ADMIN_INTELLIGENCE_LIMITS = {
  /** ~250 tokens of answer plus headroom for reasoning/formatting. */
  maxOutputTokens: 800,
  maxSummaryChars: 800,
  maxRiskChars: 300,
  maxOpportunityChars: 300,
  maxCategories: 6,
  maxAnomalyTypes: 8,
  maxCategoryNameChars: 60,
} as const;

const money = (value: number | null | undefined, currency: string): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? 'unknown'
    : `${currency} ${value.toFixed(2)}`;

const num = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? 'unknown'
    : String(Math.round(value));

const pct = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? 'unknown'
    : `${value.toFixed(1)}%`;

const clamp = (value: string, max: number): string =>
  value.length > max ? `${value.slice(0, max - 1).trimEnd()}…` : value;

export interface AdminIntelligencePromptInput {
  stats: AdminIntelligenceStats;
}

export function adminIntelligenceSystemPrompt(): string {
  return [
    'You are a platform analytics executive advisor for an event ticketing platform.',
    'You are given pre-computed platform-wide statistics. Treat them as ground truth.',
    'NEVER invent, recompute, extrapolate or contradict a number: only reference figures present in the data.',
    'Never mention or identify individual users, organizers, attendees, events or any personal data.',
    'Write in English.',
    'Respond with a JSON object exactly of the form:',
    '{"summary": string, "biggest_risk": string, "biggest_opportunity": string}',
    '"summary" is a concise executive summary of platform health, at most four sentences, specific with numbers.',
    '"biggest_risk" is one short sentence naming the single biggest risk.',
    '"biggest_opportunity" is one short sentence naming the single biggest opportunity.',
    'Do not wrap the JSON in code fences and do not add any other keys.',
  ].join(' ');
}

export function adminIntelligenceUserPrompt(input: AdminIntelligencePromptInput): string {
  const { stats } = input;
  const currency = stats.currency || 'EUR';
  const lines = [
    `Revenue last 30 days: ${money(stats.revenueLast30Days, currency)}.`,
    `Revenue previous 30 days: ${money(stats.revenuePrevious30Days, currency)}.`,
    `Revenue growth: ${pct(stats.revenueGrowthPercentage)}.`,
    `Projected revenue for the next 30 days: ${money(stats.projectedMonthlyRevenue, currency)}.`,
    `Tickets sold in the last 30 days: ${num(stats.ticketsSoldLast30Days)}.`,
    `New users in the last 30 days: ${num(stats.newUsersLast30Days)}.`,
    `New organizers in the last 30 days: ${num(stats.newOrganizersLast30Days)}.`,
    `Total events: ${num(stats.totalEvents)}; active events: ${num(stats.activeEvents)}; flagged events: ${num(stats.flaggedEvents)}.`,
    `Refunds in the last 30 days: ${num(stats.refundCount)} (${pct(stats.refundRatePercentage)} of purchases).`,
    `Detected anomalies: ${num(stats.anomalyCount)}.`,
  ];

  const anomalyTypes = (stats.anomalyTypes ?? []).slice(
    0,
    ADMIN_INTELLIGENCE_LIMITS.maxAnomalyTypes,
  );
  if (anomalyTypes.length) {
    lines.push('Anomaly breakdown:');
    for (const anomaly of anomalyTypes) {
      lines.push(`- ${anomaly.type} [${anomaly.severity}]: ${num(anomaly.count)} occurrence(s).`);
    }
  }

  const categories = (stats.topCategories ?? []).slice(
    0,
    ADMIN_INTELLIGENCE_LIMITS.maxCategories,
  );
  if (categories.length) {
    lines.push('Top categories by revenue:');
    for (const category of categories) {
      lines.push(
        `- ${clamp(category.name, ADMIN_INTELLIGENCE_LIMITS.maxCategoryNameChars)}: ${money(
          category.revenue,
          currency,
        )} across ${num(category.events)} event(s).`,
      );
    }
  }

  if (stats.totalEvents === 0) {
    lines.push('IMPORTANT: the platform has no event history yet. Give early-stage guidance.');
  }

  return lines.join('\n');
}
